import React from 'react';
import styled from 'styled-components';

import DropdownMenu from '../dropdown-menu/DropdownMenu';
import { MenuItem } from '../../types/media-preview';
import { AcrScanStatus } from '../../types/acr';
import { getAcrIconMeta } from '../../utils/acr';

import { AuditionRowProps } from './core/useAudioAudition';

/**
 * ブラウジングプレビューの試聴行（リスト表示用）。
 *
 * 「再生アイコン・ファイル名・ACR・三点リーダー」の統一レイアウト。
 * 行全体がオーディション領域（クリックで選択、Space で再生/停止、↑↓ で行移動）。
 * キーボード操作・再生制御は useAudioAudition と組で使う。
 */

export interface AudioAuditionRowComponentProps {
  fileName: string;
  /** 選択中（キーボードフォーカス対象） */
  selected: boolean;
  /** 再生中 */
  playing: boolean;
  /** useAudioAudition().getRowProps(key) の返り値 */
  rowProps: AuditionRowProps;
  /** 再生 / 停止トグル（useAudioAudition().toggle(key)） */
  onPlayToggle: () => void;
  /** 三点リーダーのメニュー項目 */
  menuItems: MenuItem[];
  acrScanStatus?: AcrScanStatus;
  /** ACR アイコンクリック（詳細情報モーダルを開く） */
  onAcrClick?: () => void;
  /** 階層インデント（px） */
  indent?: number;
  /** ファイル名の右に出す要素（文字起こしステータスチップ / 本文プレビュー等） */
  nameBadge?: React.ReactNode;
  /**
   * true のときファイル名を内容幅に留め、残り幅を nameBadge に譲る。
   * 文字起こし本文プレビューのように「名前の直後から続けて残り幅
   * いっぱいに出す」要素を渡す場合に使う。
   */
  fitFileName?: boolean;
}

const Row = styled.div<{ $indent: number; $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  padding-left: ${props => props.$indent}px;
  cursor: default;
  background-color: ${props => props.$selected ? 'rgba(0, 154, 206, 0.06)' : 'transparent'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.$selected ? 'rgba(0, 154, 206, 0.10)' : 'var(--soremo-background)'};
  }
`;

const PlayIcon = styled.span<{ $playing?: boolean }>`
  font-size: 28px;
  color: ${props => props.$playing ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'};
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: var(--soremo-blue);
    scale: 1.1;
  }
`;

const FileName = styled.span.attrs({
  className: 'bodytext-13 u-line-height-150',
})`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--soremo-black);
  font-weight: 500;
`;

const AcrBadge = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const AudioAuditionRow: React.FC<AudioAuditionRowComponentProps> = ({
  fileName,
  selected,
  playing,
  rowProps,
  onPlayToggle,
  menuItems,
  acrScanStatus,
  onAcrClick,
  indent = 0,
  nameBadge,
  fitFileName = false,
}) => {
  const acrIcon = getAcrIconMeta(acrScanStatus);
  const { ref, style, ...restRowProps } = rowProps;

  return (
    <Row
      ref={ref as React.Ref<HTMLDivElement>}
      {...restRowProps}
      style={style}
      $indent={indent}
      $selected={selected}
      role="option"
      aria-selected={selected}
    >
      <PlayIcon
        className="material-symbols-rounded"
        $playing={playing}
        onClick={(e) => {
          e.stopPropagation();
          onPlayToggle();
        }}
      >
        {playing ? 'pause_circle' : 'play_circle'}
      </PlayIcon>
      <FileName style={fitFileName ? { flex: "0 1 auto" } : undefined}>
        {fileName}
      </FileName>
      {nameBadge}
      {acrIcon.show && onAcrClick && (
        <AcrBadge
          className="material-symbols-rounded"
          style={{ color: acrIcon.color }}
          onClick={(e) => {
            e.stopPropagation();
            onAcrClick();
          }}
        >
          error
        </AcrBadge>
      )}
      <DropdownMenu items={menuItems} />
    </Row>
  );
};

export default AudioAuditionRow;
